import React, { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const JobBoard = ({ user }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    description: "",
    location: "",
    job_type: "Caregiver - Adult",
    salary: "",
    contact_type: "email",
    contact_value: "",
    image_url: "",
    expires_at: ""
  });
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axiosInstance.get("/jobs");
      setJobs(response.data);
    } catch (error) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/jobs", formData);
      toast.success("Job posted successfully!");
      setCreateOpen(false);
      setFormData({
        title: "",
        company: "",
        description: "",
        location: "",
        job_type: "Caregiver - Adult",
        salary: "",
        contact_type: "email",
        contact_value: "",
        image_url: "",
        expires_at: ""
      });
      fetchJobs();
    } catch (error) {
      toast.error("Failed to post job");
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axiosInstance.delete(`/jobs/${jobId}`);
      toast.success("Job deleted");
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div data-testid="job-board">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>Job Board</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="post-job-button" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6">
              Post a Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Post a New Job</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  data-testid="job-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  data-testid="job-company-input"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  data-testid="job-location-input"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="job_type">Job Type</Label>
                <Select value={formData.job_type} onValueChange={(value) => setFormData({...formData, job_type: value})}>
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Caregiver - Adult">Caregiver - Adult</SelectItem>
                    <SelectItem value="Caregiver - Children">Caregiver - Children</SelectItem>
                    <SelectItem value="Professional - In person">Professional - In person</SelectItem>
                    <SelectItem value="Professional - Remote">Professional - Remote</SelectItem>
                    <SelectItem value="Volunteer Help">Volunteer Help</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="salary">Salary (Optional)</Label>
                <Input
                  id="salary"
                  data-testid="job-salary-input"
                  value={formData.salary}
                  onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  placeholder="e.g., $20/hour or Volunteer"
                />
              </div>
              <div>
                <Label htmlFor="expires_at">Application Expiration Date (Optional)</Label>
                <Input
                  id="expires_at"
                  data-testid="job-expires-input"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Last date to accept applications</p>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="job-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  required
                />
              </div>
              <div>
                <Label>Image (Optional)</Label>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="image_file" className="text-sm text-gray-600">Upload from device</Label>
                    <Input
                      id="image_file"
                      data-testid="job-image-file-input"
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error("Image must be less than 2MB");
                            e.target.value = "";
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData({...formData, image_url: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max 2MB (JPG, PNG, GIF)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-xs text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div>
                    <Label htmlFor="image_url" className="text-sm text-gray-600">Paste image URL</Label>
                    <Input
                      id="image_url"
                      data-testid="job-image-url-input"
                      type="url"
                      value={formData.image_url?.startsWith('data:') ? '' : formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  {formData.image_url && (
                    <div className="mt-2">
                      <img src={formData.image_url} alt="Preview" className="max-h-32 rounded border" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="contact_type">Contact Method</Label>
                <Select value={formData.contact_type} onValueChange={(value) => setFormData({...formData, contact_type: value})}>
                  <SelectTrigger data-testid="contact-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email Address</SelectItem>
                    <SelectItem value="phone">Phone Number</SelectItem>
                    <SelectItem value="url">Website URL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact_value">
                  {formData.contact_type === "email" ? "Email Address" : formData.contact_type === "phone" ? "Phone Number" : "Application URL"}
                </Label>
                <Input
                  id="contact_value"
                  data-testid="job-contact-input"
                  type={formData.contact_type === "email" ? "email" : formData.contact_type === "phone" ? "tel" : "url"}
                  value={formData.contact_value}
                  onChange={(e) => setFormData({...formData, contact_value: e.target.value})}
                  placeholder={formData.contact_type === "email" ? "email@example.com" : formData.contact_type === "phone" ? "(555) 123-4567" : "https://..."}
                  required
                />
              </div>
              <Button data-testid="submit-job-button" type="submit" className="w-full bg-orange-500 hover:bg-orange-600">Post Job</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {jobs.map((job) => (
          <Card 
            key={job.id} 
            data-testid={`job-card-${job.id}`} 
            className="bg-white/90 backdrop-blur-sm border-orange-100 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => setSelectedJob(job)}
          >
            {job.image_url && (
              <div className="h-48 overflow-hidden rounded-t-lg">
                <img src={job.image_url} alt={job.title} className="w-full h-full object-cover" />
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-xl" style={{ fontFamily: 'Merriweather, serif' }}>{job.title}</CardTitle>
              <CardDescription className="text-base">{job.company} • {job.location}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                    {job.job_type}
                  </span>
                  {job.salary && (
                    <span className="text-sm text-gray-600">{job.salary}</span>
                  )}
                </div>
                <p className="text-gray-600 text-sm line-clamp-3">{job.description}</p>
                {job.expires_at && (
                  <p className="text-xs text-gray-500">Applications close: {new Date(job.expires_at).toLocaleDateString()}</p>
                )}
                <p className="text-xs text-gray-400">Posted: {new Date(job.created_at).toLocaleDateString()}</p>
                <div className="flex gap-2 mt-4">
                  <Button 
                    data-testid={`view-job-${job.id}`}
                    variant="default" 
                    className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedJob(job);
                    }}
                  >
                    View Details
                  </Button>
                  {(user.id === job.user_id || user.is_admin) && (
                    <Button 
                      data-testid={`delete-job-${job.id}`}
                      variant="destructive" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(job.id);
                      }}
                      className="rounded-full"
                    >
                      {user.is_admin && user.id !== job.user_id ? 'Remove' : 'Delete'}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {jobs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No jobs posted yet. Be the first to post!</p>
        </div>
      )}

      {/* Job Detail Modal */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                  {selectedJob.title}
                </DialogTitle>
                <p className="text-lg text-gray-700">
                  {selectedJob.company} • {selectedJob.location}
                </p>
              </DialogHeader>
              
              {selectedJob.image_url && (
                <div className="h-64 overflow-hidden rounded-lg mb-4">
                  <img src={selectedJob.image_url} alt={selectedJob.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                    {selectedJob.job_type}
                  </span>
                  {selectedJob.salary && (
                    <span className="text-base text-gray-700 font-medium">{selectedJob.salary}</span>
                  )}
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>Description</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.description}</p>
                  {selectedJob.expires_at && (
                    <p className="text-sm text-orange-600 mt-3 font-medium">
                      Applications close: {new Date(selectedJob.expires_at).toLocaleDateString()}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    Posted: {new Date(selectedJob.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif' }}>Contact Information</h3>
                  {selectedJob.contact_type === "email" ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Send your application to:</p>
                      <a 
                        href={`mailto:${selectedJob.contact_value}`}
                        className="text-lg text-orange-600 hover:text-orange-700 font-medium"
                        data-testid="job-email-link"
                      >
                        {selectedJob.contact_value}
                      </a>
                    </div>
                  ) : selectedJob.contact_type === "phone" ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Call to apply:</p>
                      <a 
                        href={`tel:${selectedJob.contact_value}`}
                        className="text-lg text-orange-600 hover:text-orange-700 font-medium"
                        data-testid="job-phone-link"
                      >
                        {selectedJob.contact_value}
                      </a>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Apply online at:</p>
                      <a 
                        href={selectedJob.contact_value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg text-orange-600 hover:text-orange-700 font-medium break-all"
                        data-testid="job-url-link"
                      >
                        {selectedJob.contact_value}
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  {selectedJob.contact_type === "email" ? (
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full"
                      onClick={() => window.location.href = `mailto:${selectedJob.contact_value}`}
                      data-testid="email-apply-button"
                    >
                      Email Application
                    </Button>
                  ) : selectedJob.contact_type === "phone" ? (
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full"
                      onClick={() => window.location.href = `tel:${selectedJob.contact_value}`}
                      data-testid="phone-apply-button"
                    >
                      Call Now
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full"
                      onClick={() => window.open(selectedJob.contact_value, '_blank')}
                      data-testid="url-apply-button"
                    >
                      Apply Now
                    </Button>
                  )}
                  {(user.id === selectedJob.user_id || user.is_admin) && (
                    <Button 
                      variant="destructive"
                      onClick={() => {
                        handleDelete(selectedJob.id);
                        setSelectedJob(null);
                      }}
                      className="rounded-full"
                      data-testid="delete-job-modal"
                    >
                      {user.is_admin && user.id !== selectedJob.user_id ? 'Remove Job' : 'Delete Job'}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobBoard;